import React, { useState, useRef } from 'react';
import api from '../services/api';
import { toast } from 'react-toastify';
import { FaUserTie, FaSave, FaTimes, FaPlus, FaChevronDown, FaChevronUp, FaTrash } from 'react-icons/fa';
import { data } from '../util/util';
import ReactSelect from './ReactSelect';

export default function FreelancerForm({ eventoId, onClose }) {
    const [eventoData, setEventoData] = useState({
        nome: '',
        data: '',
        dataFim: '',
        hora: ''
    });
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(false);
    const [initialLoading, setInitialLoading] = useState(true);
    const addCategoryButtonRef = useRef(null);

    // Função para formatar data para exibição (dd/mm/aaaa)
    const formatDate = (dateStr) => {
        if (!dateStr) return '';
        if (dateStr.includes('/')) return dateStr; // Já está formatada
        const [year, month, day] = dateStr.split('-');
        return `${day}/${month}/${year}`;
    };

    // Função para converter data do formato dd/mm/aaaa para yyyy-mm-dd
    const dateToInput = (dateStr) => {
        if (!dateStr) return '';
        if (dateStr.includes('-')) return dateStr; // Já está no formato correto
        const [day, month, year] = dateStr.split('/');
        return `${year}-${month}-${day}`;
    };

    // Função auxiliar para adicionar dias a uma data
    const addDays = (dateStr, days) => {
        if (!dateStr) return '';
        const date = new Date(dateStr);
        if (isNaN(date.getTime())) return '';
        date.setDate(date.getDate() + days);
        return date.toISOString().split('T')[0];
    };

    // Função para obter a data mínima para IN (7 dias antes do início do evento ou hoje, o que for maior)
    const getMinDateIn = () => {
        if (!eventoData.data) return '';
        const eventoInicio = dateToInput(eventoData.data);
        if (!eventoInicio || eventoInicio === 'undefined-undefined-undefined' || eventoInicio.includes('undefined')) {
            return new Date().toISOString().split('T')[0];
        }
        const dataMinima = addDays(eventoInicio, -7);
        const today = new Date().toISOString().split('T')[0];
        return today > dataMinima ? today : dataMinima;
    };

    // Função para obter a data máxima para IN (até a data de início do evento)
    const getMaxDateIn = () => {
        if (!eventoData.data) return '';
        const eventoInicio = dateToInput(eventoData.data);
        if (!eventoInicio || eventoInicio === 'undefined-undefined-undefined' || eventoInicio.includes('undefined')) {
            return '';
        }
        return eventoInicio;
    };

    // Função para obter a data mínima para OUT (a partir da data de fim do evento)
    const getMinDateOut = () => {
        if (!eventoData.dataFim) return '';
        const dataFim = dateToInput(eventoData.dataFim);
        if (!dataFim || dataFim === 'undefined-undefined-undefined' || dataFim.includes('undefined')) return '';
        return dataFim;
    };

    // Função para obter a data máxima para OUT (7 dias depois do fim do evento)
    const getMaxDateOut = () => {
        if (!eventoData.dataFim) return '';
        const dataFim = dateToInput(eventoData.dataFim);
        if (!dataFim || dataFim === 'undefined-undefined-undefined' || dataFim.includes('undefined')) return '';
        return addDays(dataFim, 7);
    };

    // Função para calcular quantidade de dias entre duas datas
    const calcularQuantidadeDias = (dataIn, dataOut) => {
        if (!dataIn || !dataOut) return '-';
        const dateIn = new Date(dataIn);
        const dateOut = new Date(dataOut);
        const diffTime = dateOut - dateIn;
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
        return diffDays > 0 ? diffDays : '-';
    };

    const getCategoryLetter = (index) => String.fromCharCode(65 + index);
    const getCategoryKey = (category) => String(category.id ?? category.clientKey);
    const getCategoryTitle = (category, index) => `Categoria ${getCategoryLetter(index)}: ${category.especialidade?.label || '-'}`;
    const getCategorySummary = (category) => {
        const descricao = category.descricao_servico?.trim() || 'Sem descrição';
        const quantidade = `${category.quantidade_freelancers || 0} freelancer${Number(category.quantidade_freelancers || 0) === 1 ? '' : 's'}`;
        const entrada = [category.data_in, category.hora_in].filter(Boolean).join(' ');
        const saida = [category.data_out, category.hora_out].filter(Boolean).join(' ');
        const periodo = entrada || saida ? `${entrada || '-'} \u2192 ${saida || '-'}` : '';

        return [descricao, quantidade, periodo].filter(Boolean).join(' | ');
    };

    const addCategory = () => {
        const newCategory = {
            clientKey: `new-${Date.now()}-${Math.random().toString(36).slice(2)}`,
            expanded: true,
            especialidade: null,
            descricao_servico: '',
            data_in: '',
            hora_in: '',
            data_out: '',
            hora_out: '',
            reembolso_alimentacao_transporte: 'sim',
            staff_local: 'sim',
            bilingue: 'nao',
            quantidade_freelancers: ''
        };
        setCategories([...categories, newCategory]);

        // Scroll e foco no botão após adicionar categoria
        setTimeout(() => {
            if (addCategoryButtonRef.current) {
                addCategoryButtonRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
                addCategoryButtonRef.current.focus();
            }
        }, 100);
    };

    const removeCategory = (categoryKey) => {
        setCategories(categories.filter(cat => getCategoryKey(cat) !== String(categoryKey)));
    };

    const toggleCategory = (categoryKey) => {
        setCategories(categories.map(cat =>
            getCategoryKey(cat) === String(categoryKey) ? { ...cat, expanded: !cat.expanded } : cat
        ));
    };

    const updateCategory = (categoryKey, field, value) => {
        setCategories(categories.map(cat =>
            getCategoryKey(cat) === String(categoryKey) ? { ...cat, [field]: value } : cat
        ));
    };

    const updateEspecialidade = (categoryKey, selecionado) => {
        setCategories(categories.map(cat =>
            getCategoryKey(cat) === String(categoryKey) ? {
                ...cat,
                especialidade: selecionado ?? null
            } : cat
        ));
    };

    const updateDataHorario = (categoryKey, field, value) => {
        setCategories(categories.map(cat => {
            if (getCategoryKey(cat) === String(categoryKey)) {
                return {
                    ...cat,
                    [field]: value
                };
            }
            return cat;
        }));
    };

    const buscarDadosEvento = React.useCallback(async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await api({
                method: 'get',
                action: `/evento/${eventoId}`,
                token: token
            });

            if (response.data.status && response.data.data) {
                setEventoData({
                    nome: response.data.data.nome || '',
                    data: data(response.data.data.data) || '',
                    dataFim: data(response.data.data.dataFim) || '',
                    hora: response.data.data.hora || ''
                });
            }
        } catch (error) {
            console.log('Erro ao buscar dados do evento:', error);
        }
    }, [eventoId]);

    const buscarDadosExistentes = React.useCallback(async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await api({
                method: 'get',
                action: `/eventoFreelancer/${eventoId}`,
                token: token
            });

            if (response.data.status && Array.isArray(response.data.data)) {
                const categoriasConvertidas = response.data.data.map((cat) => ({
                    id: cat.id,
                    clientKey: `db-${cat.id}`,
                    expanded: false,
                    especialidade: cat.especialidade ? {
                        value: String(cat.especialidade.id),
                        label: cat.especialidade.nome
                    } : null,
                    descricao_servico: cat.descricao_servico ?? '',
                    data_in: cat.data_in ?? '',
                    hora_in: cat.hora_in ?? '',
                    data_out: cat.data_out ?? '',
                    hora_out: cat.hora_out ?? '',
                    reembolso_alimentacao_transporte: cat.reembolso_alimentacao_transporte ?? 'sim',
                    staff_local: cat.staff_local ?? 'sim',
                    bilingue: cat.bilingue ?? 'nao',
                    quantidade_freelancers: cat.quantidade_freelancers ?? '',
                }));

                setCategories(categoriasConvertidas);
            }
        } catch (error) {
            console.log('Nenhum dado existente encontrado ou erro ao buscar:', error);
        } finally {
            setInitialLoading(false);
        }
    }, [eventoId]);

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (categories.length === 0) {
            toast.error('Adicione pelo menos uma categoria');
            return;
        }

        // Validar categorias
        for (let i = 0; i < categories.length; i++) {
            const cat = categories[i];
            if (!cat.especialidade?.value) {
                toast.error(`Categoria ${getCategoryLetter(i)}: Tipo de serviço é obrigatória`);
                return;
            }
            if (!cat.descricao_servico.trim()) {
                toast.error(`Categoria ${getCategoryLetter(i)}: Descrição do serviço é obrigatória`);
                return;
            }
            if (!cat.quantidade_freelancers) {
                toast.error(`Categoria ${getCategoryLetter(i)}: Quantidade de freelancers é obrigatória`);
                return;
            }
            if (!cat.data_in || !cat.hora_in || !cat.data_out || !cat.hora_out) {
                toast.error(`Categoria ${getCategoryLetter(i)}: Preencha todos os campos de data/horário`);
                return;
            }

            // Validar se as datas estão dentro do intervalo permitido
            // Entrada: até 7 dias antes do início do evento (não pode ser depois)
            if (eventoData.data) {
                const eventoInicio = dateToInput(eventoData.data);
                if (eventoInicio && eventoInicio !== 'undefined-undefined-undefined' && !eventoInicio.includes('undefined')) {
                    const dataInicioEvento = new Date(eventoInicio);
                    const dataInicioMin = new Date(eventoInicio);
                    dataInicioMin.setDate(dataInicioMin.getDate() - 7);
                    const dataIn = new Date(cat.data_in);

                    if (dataIn < dataInicioMin || dataIn > dataInicioEvento) {
                        toast.error(`Categoria ${getCategoryLetter(i)}: A data de entrada deve ser até no máximo 7 dias antes do início do evento`);
                        return;
                    }
                }
            }

            // Saída: até 7 dias depois do fim do evento (não pode ser antes)
            if (eventoData.dataFim) {
                const eventoFim = dateToInput(eventoData.dataFim);
                if (eventoFim && eventoFim !== 'undefined-undefined-undefined' && !eventoFim.includes('undefined')) {
                    const dataFimEvento = new Date(eventoFim);
                    const dataFimMax = new Date(eventoFim);
                    dataFimMax.setDate(dataFimMax.getDate() + 7);
                    const dataOut = new Date(cat.data_out);

                    if (dataOut < dataFimEvento || dataOut > dataFimMax) {
                        toast.error(`Categoria ${getCategoryLetter(i)}: A data de saída deve ser até no máximo 7 dias depois do fim do evento`);
                        return;
                    }
                }
            }
        }

        setLoading(true);

        const token = localStorage.getItem('token');

        try {
            const categoriasParaEnviar = categories.map(({ clientKey, expanded, ...cat }, index) => ({
                ...cat,
                ...(cat.id ? { id: cat.id } : {}),
                ordem: index + 1,
                especialidade: cat.especialidade?.value || null,
                descricao_servico: cat.descricao_servico?.trim() || null,
                data_in: cat.data_in || null,
                hora_in: cat.hora_in || null,
                data_out: cat.data_out || null,
                hora_out: cat.hora_out || null,
                reembolso_alimentacao_transporte: cat.reembolso_alimentacao_transporte || null,
                staff_local: cat.staff_local || null,
                bilingue: cat.bilingue || null,
                quantidade_freelancers: cat.quantidade_freelancers ? Number(cat.quantidade_freelancers) : null,
            }));

            const response = await api({
                method: 'put',
                action: `/eventoFreelancer/${eventoId}`,
                token,
                data: categoriasParaEnviar,
            });

            if (response.data.status) {
                toast.success('Dados salvos e email enviado com sucesso!');
                onClose();
            } else {
                toast.error(response.data.msg || 'Erro ao enviar solicitação');
            }
        } catch (error) {
            toast.error(error?.response?.data?.mensagem || 'Erro ao enviar solicitação');
        } finally {
            setLoading(false);
        }
    };

    // Buscar dados do evento e dados existentes quando o componente carregar
    React.useEffect(() => {
        const loadData = async () => {
            await buscarDadosEvento();
            await buscarDadosExistentes();
        };
        loadData();
    }, [buscarDadosEvento, buscarDadosExistentes]);

    if (initialLoading) {
        return (
            <div className="text-center p-4">
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Carregando...</span>
                </div>
                <p className="mt-2">Carregando dados...</p>
            </div>
        );
    }

    return (
        <form onSubmit={handleSubmit}>
            {/* Informações do Evento - Preenchidas automaticamente */}
            <div className="card mb-4">
                <div className="card-body">
                    <h6 className="card-title mb-3">Informações do Evento</h6>
                    <div className="row">
                        <div className="col-sm-4">
                            <div className="form-group">
                                <label className="form-label">Data de Início</label>
                                <div className="form-control-plaintext">
                                    {formatDate(eventoData.data) || '-'}
                                </div>
                            </div>
                        </div>
                        <div className="col-sm-4">
                            <div className="form-group">
                                <label className="form-label">Data de Finalização</label>
                                <div className="form-control-plaintext">
                                    {formatDate(eventoData.dataFim) || '-'}
                                </div>
                            </div>
                        </div>
                        <div className="col-sm-4">
                            <div className="form-group">
                                <label className="form-label">Horário do Evento</label>
                                <div className="form-control-plaintext">
                                    {eventoData.hora || '-'}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {categories.length === 0 && (
                <div className="alert alert-info text-center">
                    <p className="mb-0">Nenhuma categoria adicionada</p>
                    <small>Clique em "Adicionar Categoria" para começar</small>
                </div>
            )}

            {/* Categorias */}
            <div className="mb-4">
                {categories.map((category, index) => (
                    <div key={getCategoryKey(category)} className="card mb-3">
                        {/* Cabeçalho da Categoria */}
                        <div
                            className="card-header d-flex justify-content-between align-items-center"
                            style={{ cursor: 'pointer', backgroundColor: '#f8f9fa', padding: '0.75rem 1.25rem' }}
                            onClick={() => toggleCategory(getCategoryKey(category))}
                        >
                            <div className="d-flex flex-column">
                                <span className="fw-semibold text-primary">
                                    {getCategoryTitle(category, index)}
                                </span>
                                {!category.expanded && (
                                    <small className="text-muted mt-1">
                                        {getCategorySummary(category)}
                                    </small>
                                )}
                            </div>
                            <div className="d-flex align-items-center gap-2">
                                <button
                                    type="button"
                                    className="btn btn-sm btn-outline-danger"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        removeCategory(getCategoryKey(category));
                                    }}
                                >
                                    <FaTrash />
                                </button>
                                {category.expanded ? <FaChevronUp /> : <FaChevronDown />}
                            </div>
                        </div>

                        {/* Conteúdo da Categoria */}
                        {category.expanded && (
                            <div className="card-body">
                                <div className="row gx-3 mb-3">
                                    <div className="col-sm-6">
                                        <label className="form-label">
                                            Tipo de Serviço <span className="text-danger">*</span>
                                        </label>
                                        <ReactSelect
                                            value={category.especialidade}
                                            onChange={(selecionado) => updateEspecialidade(getCategoryKey(category), selecionado)}
                                            loadOptions={(input) => {
                                                const token = localStorage.getItem('token');
                                                return api({
                                                    method: 'get',
                                                    action: '/freelaEspecialidade',
                                                    token: token,
                                                    params: { nome: input }
                                                }).then((response) => {
                                                    return (response.data.data ?? []).map((tipo) => ({
                                                        value: String(tipo.id),
                                                        label: tipo.nome
                                                    }));
                                                }).catch(() => []);
                                            }}
                                        />
                                    </div>
                                    <div className="col-sm-6">
                                        <label className="form-label">
                                            Quantidade de Freelancers <span className="text-danger">*</span>
                                        </label>
                                        <input
                                            type="number"
                                            className="form-control"
                                            value={category.quantidade_freelancers}
                                            onChange={(e) => updateCategory(getCategoryKey(category), 'quantidade_freelancers', e.target.value)}
                                            placeholder="Ex: 10"
                                            min="1"
                                            required
                                        />
                                    </div>
                                </div>

                                {/* Descrição do Serviço */}
                                <div className="mb-3">
                                    <label className="form-label">
                                        <FaUserTie className="me-2" />
                                        Descrição do Serviço <span className="text-danger">*</span>
                                    </label>
                                    <textarea
                                        className="form-control"
                                        value={category.descricao_servico}
                                        onChange={(e) => updateCategory(getCategoryKey(category), 'descricao_servico', e.target.value)}
                                        placeholder="Descreva brevemente o serviço que precisa de freelancer..."
                                        rows={3}
                                        required
                                    />
                                </div>

                                <div className="mb-3">
                                    <div className="row g-3">
                                        {/* Entrada - Data e Hora */}
                                        <div className="col-12 col-lg-6">
                                            <label className="form-label">Entrada</label>
                                            <div className="d-flex gap-2">
                                                <input
                                                    type="date"
                                                    className="form-control"
                                                    value={category.data_in}
                                                    min={getMinDateIn()}
                                                    max={getMaxDateIn()}
                                                    onChange={(e) => updateDataHorario(getCategoryKey(category), 'data_in', e.target.value)}
                                                    required
                                                />
                                                <input
                                                    type="time"
                                                    className="form-control"
                                                    value={category.hora_in}
                                                    onChange={(e) => updateDataHorario(getCategoryKey(category), 'hora_in', e.target.value)}
                                                    required
                                                />
                                            </div>
                                        </div>
                                        {/* Saída - Data e Hora */}
                                        <div className="col-12 col-lg-6">
                                            <label className="form-label">Saída</label>
                                            <div className="d-flex gap-2">
                                                <input
                                                    type="date"
                                                    className="form-control"
                                                    value={category.data_out}
                                                    min={getMinDateOut()}
                                                    max={getMaxDateOut()}
                                                    onChange={(e) => updateDataHorario(getCategoryKey(category), 'data_out', e.target.value)}
                                                    required
                                                />
                                                <input
                                                    type="time"
                                                    className="form-control"
                                                    value={category.hora_out}
                                                    onChange={(e) => updateDataHorario(getCategoryKey(category), 'hora_out', e.target.value)}
                                                    required
                                                />
                                            </div>
                                        </div>
                                    </div>
                                    <div className="row g-2 mt-2">
                                        {/* Quantidade de Dias - Calculado */}
                                        <div className="col-md-6">
                                            <div className="d-flex align-items-center gap-2">
                                                <label className="form-label mb-0" style={{ minWidth: '80px' }}>Dias:</label>
                                                <div className="form-control-plaintext fw-bold">
                                                    {calcularQuantidadeDias(category.data_in, category.data_out)}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Reembolso, Staff Local, Bilíngue */}
                                <div className="row gy-3 mb-3">
                                    <div className="col-sm-6">
                                        <label className="form-label">Reembolso alimentação e transporte</label>
                                        <select
                                            className="form-select"
                                            value={category.reembolso_alimentacao_transporte}
                                            onChange={(e) => updateCategory(getCategoryKey(category), 'reembolso_alimentacao_transporte', e.target.value)}
                                        >
                                            <option value="sim">Sim</option>
                                            <option value="nao">Não</option>
                                        </select>
                                    </div>
                                    <div className="col-sm-6">
                                        <label className="form-label">Staff Local</label>
                                        <select
                                            className="form-select"
                                            value={category.staff_local}
                                            onChange={(e) => updateCategory(getCategoryKey(category), 'staff_local', e.target.value)}
                                        >
                                            <option value="sim">Sim</option>
                                            <option value="nao">Não</option>
                                        </select>
                                    </div>
                                    <div className="col-sm-6">
                                        <label className="form-label">Bilíngue</label>
                                        <select
                                            className="form-select"
                                            value={category.bilingue}
                                            onChange={(e) => updateCategory(getCategoryKey(category), 'bilingue', e.target.value)}
                                        >
                                            <option value="nao">Não</option>
                                            <option value="sim">Sim</option>
                                        </select>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                ))}
            </div>

            {/* Botão Adicionar Categoria */}
            <div className="mb-3">
                <button
                    ref={addCategoryButtonRef}
                    type="button"
                    className="btn btn-primary"
                    onClick={addCategory}
                >
                    <FaPlus className="me-2" />
                    Adicionar Categoria
                </button>
            </div>

            {/* Botões de Ação */}
            <div className="row mt-4">
                <div className="col-sm-12">
                    <div className="d-flex justify-content-end gap-2">
                        <button
                            type="button"
                            className="btn btn-secondary"
                            onClick={onClose}
                            disabled={loading}
                        >
                            <FaTimes className="me-2" />
                            Cancelar
                        </button>
                        <button
                            type="submit"
                            className="btn btn-primary"
                            disabled={loading}
                        >
                            <FaSave className="me-2" />
                            {loading ? 'Enviando...' : 'Enviar Solicitação'}
                        </button>
                    </div>
                </div>
            </div>
        </form>
    );
}
